import { addToScene } from '../utils/forgeUtils';
const THREE = window.THREE;

/**
 * Screen pixels per viewer unit at the current zoom of a 2D viewer.
 * @param {Object} viewer - Forge viewer
 * @returns {number}
 */
export function viewerPxPerUnit(viewer) {
    return viewer.worldToClient(new THREE.Vector3(1, 0, 0)).x - viewer.worldToClient(new THREE.Vector3(0, 0, 0)).x;
}

/**
 * The count drawn on each cluster icon of a 2D viewer, kept at a fixed size in pixels.
 *
 * Each distinct text is triangulated once at size 1 and shared by every label that shows it, as is
 * the one material. A label is sized by scaling its mesh, so `rescale()` on a zoom tick costs a few
 * assignments per label instead of new geometry. The shared geometry and material are only freed by
 * `dispose()`: never remove these meshes with `removeFromScene`, which disposes them.
 */
export class ClusterCountLabels {
    /**
     * @param {Object} viewer - Forge viewer
     * @param {string} sceneName - Overlay scene the labels are drawn in
     * @param {{ pixelSize?: number, color?: number }} [options]
     */
    constructor(viewer, sceneName, { pixelSize = 12, color = 0xffffff } = {}) {
        this.viewer = viewer;
        this.sceneName = sceneName;
        this.pixelSize = pixelSize;
        this.material = new THREE.MeshBasicMaterial({ color, depthTest: false, depthWrite: false });
        this.geometries = new Map();
        this.meshes = [];
    }

    /**
     * @param {string|number} text
     * @param {{ x: number, y: number, z: number }} center - Centre of the label in viewer units
     */
    add(text, center) {
        const shape = this.shapeFor(String(text));
        const mesh = new THREE.Mesh(shape.geometry, this.material);
        mesh.userData.clusterLabel = { center: { x: center.x, y: center.y, z: center.z }, shape };
        this.place(mesh, this.pixelSize / viewerPxPerUnit(this.viewer));
        addToScene(mesh, this.viewer, this.sceneName);
        this.meshes.push(mesh);
    }

    /** Keep every label at `pixelSize` after the zoom changed. */
    rescale() {
        if (!this.meshes.length) return;
        const scale = this.pixelSize / viewerPxPerUnit(this.viewer);
        this.meshes.forEach((mesh) => this.place(mesh, scale));
    }

    /** Remove every label from the scene; the shared geometry and material stay for reuse. */
    clear() {
        const scene = this.viewer.impl?.overlayScenes[this.sceneName]?.scene;
        this.meshes.forEach((mesh) => scene?.remove(mesh));
        this.meshes = [];
    }

    dispose() {
        this.clear();
        this.geometries.forEach(({ geometry }) => geometry.dispose());
        this.geometries.clear();
        this.material.dispose();
    }

    shapeFor(text) {
        let shape = this.geometries.get(text);
        if (!shape) {
            const geometry = new THREE.TextGeometry(text, { font: 'artifakt element', size: 1, height: 0, curveSegments: 3 });
            geometry.computeBoundingBox();
            const { min, max } = geometry.boundingBox;
            shape = { geometry, midX: (min.x + max.x) / 2, midY: (min.y + max.y) / 2 };
            this.geometries.set(text, shape);
        }
        return shape;
    }

    place(mesh, scale) {
        const { center, shape } = mesh.userData.clusterLabel;
        mesh.scale.set(scale, scale, 1);
        mesh.position.set(center.x - shape.midX * scale, center.y - shape.midY * scale, center.z);
    }
}
